import { useState, useEffect, type ReactNode } from 'react';
import { Modal } from '../../components/Modal';
import { useComplianceProfile, useUpsertComplianceProfile, useDeleteComplianceProfile } from '../hooks';
import type {
  ComplianceProfile, UpsertComplianceProfilePayload,
  CpIndustrySector, CpRegulationType, CpNis2Sector, CpDoraApplicable,
  CpEmployeeCountRange, CpRevenueRange, CpBalanceSheetRange,
  CpLocationScope, CpCertification, CpIsmsStatus, CpBcmsStatus,
  CpQuality, CpSizeClass,
} from '../types';
import styles from './ComplianceProfileTab.module.css';

// ── Label maps ────────────────────────────────────────────────────────────────

const INDUSTRY_SECTOR_LABELS: Record<CpIndustrySector, string> = {
  financial: 'Finanz- und Versicherungswesen',
  financial_service_provider: 'Finanzdienstleistungsanbieter (IKT)',
  healthcare: 'Gesundheitswesen',
  energy: 'Energie',
  critical_infrastructure: 'Kritische Infrastruktur (KRITIS)',
  digital_product_manufacturer: 'Hersteller digitaler Produkte (CRA)',
  manufacturing: 'Verarbeitendes Gewerbe',
  public_sector: 'Öffentliche Verwaltung',
  research: 'Forschung und Wissenschaft',
  other: 'Sonstige',
};

const REG_TYPE_LABELS: Record<CpRegulationType, string> = {
  financial_institution: 'Finanzinstitut (DORA)',
  ict_provider_financial: 'IKT-Dienstleister (Finanzsektor)',
  nis2_essential: 'NIS2 — Wesentliche Einrichtung',
  nis2_important: 'NIS2 — Wichtige Einrichtung',
  unregulated: 'Nicht reguliert',
  unknown: 'Unbekannt',
};

const NIS2_SECTOR_LABELS: Record<CpNis2Sector, string> = {
  energie: 'Energie',
  verkehr: 'Verkehr',
  bankwesen: 'Bankwesen',
  finanzmarktinfrastruktur: 'Finanzmarktinfrastruktur',
  gesundheit: 'Gesundheit',
  trinkwasser: 'Trinkwasser',
  abwasser: 'Abwasser',
  digitale_infrastruktur: 'Digitale Infrastruktur',
  ikt_dienste_b2b: 'IKT-Dienste (B2B)',
  oeffentliche_verwaltung: 'Öffentliche Verwaltung',
  weltraum: 'Weltraum',
  post_kurier: 'Post und Kurier',
  abfallbewirtschaftung: 'Abfallbewirtschaftung',
  chemie: 'Chemie',
  lebensmittel: 'Lebensmittel',
  verarbeitendes_gewerbe: 'Verarbeitendes Gewerbe',
  digitale_dienste: 'Digitale Dienste',
  forschung: 'Forschung',
};

const DORA_LABELS: Record<CpDoraApplicable, string> = {
  yes: 'Ja — direkt anwendbar',
  no: 'Nein',
  indirect: 'Indirekt (IKT-Drittdienstleister)',
};

const EMP_LABELS: Record<CpEmployeeCountRange, string> = {
  lt_10: '< 10 Mitarbeiter',
  e10_49: '10–49 Mitarbeiter',
  e50_249: '50–249 Mitarbeiter',
  e250_999: '250–999 Mitarbeiter',
  gte_1000: '≥ 1.000 Mitarbeiter',
};

const REV_LABELS: Record<CpRevenueRange, string> = {
  lt_2m: '< 2 Mio. €',
  r2m_10m: '2–10 Mio. €',
  r10m_50m: '10–50 Mio. €',
  r50m_250m: '50–250 Mio. €',
  gte_250m: '≥ 250 Mio. €',
};

const BAL_LABELS: Record<CpBalanceSheetRange, string> = {
  lt_2m: '< 2 Mio. €',
  b2m_10m: '2–10 Mio. €',
  b10m_43m: '10–43 Mio. €',
  b43m_250m: '43–250 Mio. €',
  gte_250m: '≥ 250 Mio. €',
};

const LOC_LABELS: Record<CpLocationScope, string> = {
  de_only: 'Deutschland',
  eu: 'EU / Europa',
  global: 'Global',
};

const CERT_LABELS: Record<CpCertification, string> = {
  iso_27001: 'ISO 27001',
  iso_22301: 'ISO 22301',
  iso_9001: 'ISO 9001',
  tisax: 'TISAX',
  soc_2: 'SOC 2',
  none: 'Keine',
};

const ISMS_LABELS: Record<CpIsmsStatus, string> = {
  not_existing: 'Nicht vorhanden',
  documented: 'Dokumentiert',
  certified: 'Zertifiziert (ISO 27001)',
};

const BCMS_LABELS: Record<CpBcmsStatus, string> = {
  not_existing: 'Nicht vorhanden',
  documented: 'Dokumentiert',
  certified: 'Zertifiziert (ISO 22301)',
};

const SIZE_LABELS: Record<CpSizeClass, string> = {
  S: 'Klein (S)',
  M: 'Mittel (M)',
  L: 'Groß (L)',
};

const QUALITY_COLORS: Record<CpQuality, { bg: string; color: string }> = {
  known:      { bg: '#dcfce7', color: '#15803d' },
  estimated:  { bg: '#fef3c7', color: '#92400e' },
  cp_default: { bg: '#f3f4f6', color: '#6b7280' },
};

const SIZE_COLORS: Record<CpSizeClass, { bg: string; color: string }> = {
  S: { bg: '#dcfce7', color: '#15803d' },
  M: { bg: '#dbeafe', color: '#1e40af' },
  L: { bg: '#fce7f3', color: '#9d174d' },
};

type SectionTab = 'regulierung' | 'groesse' | 'struktur' | 'reifegrad';

const SECTION_TABS: { id: SectionTab; label: string }[] = [
  { id: 'regulierung', label: 'Regulierung' },
  { id: 'groesse', label: 'Größenklasse' },
  { id: 'struktur', label: 'Struktur' },
  { id: 'reifegrad', label: 'IT-Reifegrad' },
];

// ── Default form state ────────────────────────────────────────────────────────

const DEFAULT_QUALITIES: Partial<UpsertComplianceProfilePayload> = {
  industrySectorQuality: 'cp_default',
  isDigitalProductManufacturerQuality: 'cp_default',
  regulationTypeQuality: 'cp_default',
  nis2SectorQuality: 'cp_default',
  bafinSupervisedQuality: 'cp_default',
  doraApplicableQuality: 'cp_default',
  employeeCountRangeQuality: 'cp_default',
  revenueRangeQuality: 'cp_default',
  balanceSheetRangeQuality: 'cp_default',
  locationScopeQuality: 'cp_default',
  criticalFunctionsCountQuality: 'cp_default',
  criticalIctProvidersCountQuality: 'cp_default',
  certifications: [],
  certificationsQuality: 'cp_default',
  ismsStatusQuality: 'cp_default',
  bcmsStatusQuality: 'cp_default',
  lastAuditDateQuality: 'cp_default',
};

function profileToForm(p: ComplianceProfile): UpsertComplianceProfilePayload {
  return {
    industrySector: p.industrySector ?? undefined,
    industrySectorQuality: p.industrySectorQuality,
    isDigitalProductManufacturer: p.isDigitalProductManufacturer ?? undefined,
    isDigitalProductManufacturerQuality: p.isDigitalProductManufacturerQuality,
    regulationType: p.regulationType ?? undefined,
    regulationTypeQuality: p.regulationTypeQuality,
    nis2Sector: p.nis2Sector ?? undefined,
    nis2SectorQuality: p.nis2SectorQuality,
    bafinSupervised: p.bafinSupervised ?? undefined,
    bafinSupervisedQuality: p.bafinSupervisedQuality,
    doraApplicable: p.doraApplicable ?? undefined,
    doraApplicableQuality: p.doraApplicableQuality,
    employeeCountRange: p.employeeCountRange ?? undefined,
    employeeCountRangeQuality: p.employeeCountRangeQuality,
    revenueRange: p.revenueRange ?? undefined,
    revenueRangeQuality: p.revenueRangeQuality,
    balanceSheetRange: p.balanceSheetRange ?? undefined,
    balanceSheetRangeQuality: p.balanceSheetRangeQuality,
    locationScope: p.locationScope ?? undefined,
    locationScopeQuality: p.locationScopeQuality,
    criticalFunctionsCount: p.criticalFunctionsCount ?? undefined,
    criticalFunctionsCountQuality: p.criticalFunctionsCountQuality,
    criticalIctProvidersCount: p.criticalIctProvidersCount ?? undefined,
    criticalIctProvidersCountQuality: p.criticalIctProvidersCountQuality,
    certifications: p.certifications,
    certificationsQuality: p.certificationsQuality,
    ismsStatus: p.ismsStatus ?? undefined,
    ismsStatusQuality: p.ismsStatusQuality,
    bcmsStatus: p.bcmsStatus ?? undefined,
    bcmsStatusQuality: p.bcmsStatusQuality,
    lastAuditDate: p.lastAuditDate ?? undefined,
    lastAuditDateQuality: p.lastAuditDateQuality,
  };
}

// ── Score computation ─────────────────────────────────────────────────────────

function computeScore(form: UpsertComplianceProfilePayload): number {
  let score = 0;
  if (form.ismsStatus === 'certified') score += 30;
  else if (form.ismsStatus === 'documented') score += 15;
  if (form.bcmsStatus === 'certified') score += 20;
  else if (form.bcmsStatus === 'documented') score += 10;
  const activeCerts = (form.certifications ?? []).filter((c) => c !== 'none');
  score += Math.min(activeCerts.length * 10, 30);
  if (form.lastAuditDate) {
    const yearsAgo = (Date.now() - new Date(form.lastAuditDate).getTime()) / (1000 * 60 * 60 * 24 * 365.25);
    score += yearsAgo < 1 ? 20 : yearsAgo < 3 ? 15 : 5;
  }
  return score;
}

function scoreProps(score: number): { color: string; label: string } {
  if (score >= 76) return { color: '#16a34a', label: 'Optimiert' };
  if (score >= 51) return { color: '#2563eb', label: 'Fortgeschritten' };
  if (score >= 26) return { color: '#d97706', label: 'Im Aufbau' };
  return { color: '#dc2626', label: 'Grundlegend' };
}

function subScoreProps(score: number, max: number): { color: string; label: string } {
  const pct = max > 0 ? score / max : 0;
  if (pct >= 0.75) return { color: '#16a34a', label: 'Optimiert' };
  if (pct >= 0.50) return { color: '#2563eb', label: 'Fortgeschritten' };
  if (pct > 0)     return { color: '#d97706', label: 'Im Aufbau' };
  return { color: '#dc2626', label: 'Grundlegend' };
}

function computeSizeClass(form: UpsertComplianceProfilePayload): CpSizeClass {
  const emp = form.employeeCountRange;
  const rev = form.revenueRange;
  const bal = form.balanceSheetRange;
  const isSmall = emp === 'lt_10' && rev === 'lt_2m' && bal === 'lt_2m';
  const isLarge =
    emp === 'e250_999' || emp === 'gte_1000' ||
    rev === 'r50m_250m' || rev === 'gte_250m' ||
    bal === 'b43m_250m' || bal === 'gte_250m';
  if (isSmall) return 'S';
  if (isLarge) return 'L';
  return 'M';
}

// ── Helper components ─────────────────────────────────────────────────────────

function ScoreRing({ score, color }: { score: number; color: string }) {
  const r = 22;
  const circ = 2 * Math.PI * r;
  return (
    <svg width={56} height={56} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={28} cy={28} r={r} fill="none" stroke="var(--color-border)" strokeWidth={5} />
      <circle
        cx={28} cy={28} r={r}
        fill="none"
        stroke={color}
        strokeWidth={5}
        strokeLinecap="round"
        strokeDasharray={circ}
        strokeDashoffset={circ * (1 - score / 100)}
        style={{ transition: 'stroke-dashoffset 0.5s ease' }}
      />
    </svg>
  );
}

function SubScoreCard({ label, score, max }: { label: string; score: number; max: number }) {
  const { color, label: levelLabel } = subScoreProps(score, max);
  const pct = max > 0 ? Math.round((score / max) * 100) : 0;
  return (
    <div className={styles.subScoreCard}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{ fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)' }}>
          {label}
        </span>
        <span style={{ fontSize: '0.875rem', fontWeight: 700, color, lineHeight: 1 }}>
          {score}
          <span style={{ fontSize: '0.6875rem', fontWeight: 500, color: 'var(--color-text-muted)' }}>
            /{max}
          </span>
        </span>
      </div>
      <div style={{ height: 5, background: 'var(--color-border)', borderRadius: 999, overflow: 'hidden' }}>
        <div style={{ height: '100%', borderRadius: 999, background: color, width: `${pct}%`, transition: 'width 0.4s ease' }} />
      </div>
      <span style={{ fontSize: '0.6875rem', fontWeight: 600, color }}>{levelLabel}</span>
    </div>
  );
}

const QUALITY_CYCLE: CpQuality[] = ['cp_default', 'estimated', 'known'];

function FieldCard({
  label, help, valueNode, quality, onQuality, scoreText,
}: {
  label: string;
  help: string;
  valueNode: ReactNode;
  quality: CpQuality;
  onQuality: (v: CpQuality) => void;
  scoreText?: string;
}) {
  const qc = QUALITY_COLORS[quality];
  function cycleQuality() {
    const idx = QUALITY_CYCLE.indexOf(quality);
    onQuality(QUALITY_CYCLE[(idx + 1) % QUALITY_CYCLE.length]);
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--color-text-primary)' }}>
            {label}
          </span>
          {scoreText !== undefined && (
            <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{scoreText}</span>
          )}
        </div>
        <button
          type="button"
          onClick={cycleQuality}
          title="Klicken zum Wechseln: Standard → Geschätzt → Bekannt"
          style={{
            padding: '0.125rem 0.5rem',
            fontSize: '0.6875rem',
            fontWeight: 600,
            borderRadius: 9999,
            border: 'none',
            cursor: 'pointer',
            background: qc.bg,
            color: qc.color,
            flexShrink: 0,
          }}
        >
          {QUALITY_LABELS[quality]}
        </button>
      </div>
      {valueNode}
      <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', margin: '0.375rem 0 0', lineHeight: 1.4 }}>
        {help}
      </p>
    </div>
  );
}

function EnumSelect<T extends string>({
  value, options, labels, placeholder, onChange,
}: {
  value: T | undefined;
  options: T[];
  labels: Record<T, string>;
  placeholder: string;
  onChange: (v: T | undefined) => void;
}) {
  return (
    <select
      value={value ?? ''}
      onChange={(e) => onChange((e.target.value || undefined) as T | undefined)}
      className={styles.fieldSelect}
      style={{ width: '100%', maxWidth: 'none' }}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o} value={o}>{labels[o]}</option>
      ))}
    </select>
  );
}

// ── Quality labels (shared) ───────────────────────────────────────────────────

const QUALITY_LABELS: Record<CpQuality, string> = {
  known: 'Bekannt',
  estimated: 'Geschätzt',
  cp_default: 'Standard',
};

function ProfileDisplayRow({
  label, value, quality,
}: {
  label: string;
  value: string | null;
  quality: CpQuality;
}) {
  return (
    <div className={styles.displayRow}>
      <span className={styles.displayLabel}>{label}</span>
      {value
        ? <span className={styles.displayValue}>{value}</span>
        : <span className={styles.displayValueEmpty}>—</span>
      }
      <span
        className={styles.qualityBadge}
        style={{ background: QUALITY_COLORS[quality].bg, color: QUALITY_COLORS[quality].color }}
      >
        {QUALITY_LABELS[quality]}
      </span>
    </div>
  );
}

function ProfileSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className={styles.displayCard}>
      <div className={styles.displayCardTitle}>{title}</div>
      {children}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function ComplianceProfileTab({ companyId }: { companyId: string }) {
  const { data: response, isLoading } = useComplianceProfile(companyId);
  const profile = response?.data ?? null;

  const [activeSection, setActiveSection] = useState<SectionTab>('regulierung');
  const [form, setForm] = useState<UpsertComplianceProfilePayload>({ ...DEFAULT_QUALITIES });
  const [isDirty, setIsDirty] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isEditOpen, setEditOpen] = useState(false);
  const [isDeleteOpen, setDeleteOpen] = useState(false);

  const upsert = useUpsertComplianceProfile(companyId);
  const deleteMutation = useDeleteComplianceProfile(companyId);

  useEffect(() => {
    if (profile) {
      setForm(profileToForm(profile));
      setIsDirty(false);
    }
  }, [profile]);

  function set<K extends keyof UpsertComplianceProfilePayload>(key: K, value: UpsertComplianceProfilePayload[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setIsDirty(true);
  }

  function handleOpenEdit() {
    setForm(profile ? profileToForm(profile) : { ...DEFAULT_QUALITIES });
    setIsDirty(false);
    setSaveError(null);
    setEditOpen(true);
  }

  function handleCloseEdit() {
    setForm(profile ? profileToForm(profile) : { ...DEFAULT_QUALITIES });
    setIsDirty(false);
    setSaveError(null);
    setEditOpen(false);
  }

  async function handleDeleteConfirm() {
    await deleteMutation.mutateAsync();
    setDeleteOpen(false);
  }

  async function handleSave() {
    setSaveError(null);
    try {
      await upsert.mutateAsync(form);
      setIsDirty(false);
      setEditOpen(false);
    } catch {
      setSaveError('Speichern fehlgeschlagen. Bitte erneut versuchen.');
    }
  }

  const q = (field: keyof UpsertComplianceProfilePayload) =>
    (form[field] as CpQuality | undefined) ?? 'cp_default';

  const isNis2 = form.regulationType === 'nis2_essential' || form.regulationType === 'nis2_important';

  const knownCount = [
    'industrySectorQuality', 'isDigitalProductManufacturerQuality',
    'regulationTypeQuality', 'nis2SectorQuality', 'bafinSupervisedQuality', 'doraApplicableQuality',
    'employeeCountRangeQuality', 'revenueRangeQuality', 'balanceSheetRangeQuality',
    'locationScopeQuality', 'criticalFunctionsCountQuality', 'criticalIctProvidersCountQuality',
    'certificationsQuality', 'ismsStatusQuality', 'bcmsStatusQuality', 'lastAuditDateQuality',
  ].filter((f) => (form as any)[f] === 'known').length;

  const score = computeScore(form);
  const { color: scoreColor, label: scoreLabel } = scoreProps(score);
  const liveSizeClass = computeSizeClass(form);

  // Sub-scores for detail box
  const ismsScore = form.ismsStatus === 'certified' ? 30 : form.ismsStatus === 'documented' ? 15 : 0;
  const bcmsScore = form.bcmsStatus === 'certified' ? 20 : form.bcmsStatus === 'documented' ? 10 : 0;
  const certScore = Math.min((form.certifications ?? []).filter((c) => c !== 'none').length * 10, 30);
  const auditScore = (() => {
    if (!form.lastAuditDate) return 0;
    const yearsAgo = (Date.now() - new Date(form.lastAuditDate).getTime()) / (1000 * 60 * 60 * 24 * 365.25);
    return yearsAgo < 1 ? 20 : yearsAgo < 3 ? 15 : 5;
  })();

  if (isLoading) {
    return <div style={{ padding: '2rem', color: 'var(--color-text-muted)' }}>Lade Compliance-Profil…</div>;
  }

  // ── Read-only value helpers ──────────────────────────────────────────────────

  const pq = (field: keyof ComplianceProfile): CpQuality =>
    profile ? ((profile as any)[field + 'Quality'] as CpQuality) ?? 'cp_default' : 'cp_default';

  const dn = <T extends string>(v: T | null | undefined, labels: Record<T, string>) =>
    v ? (labels[v] ?? v) : null;

  const db = (v: boolean | null | undefined) =>
    v === true ? 'Ja' : v === false ? 'Nein' : null;

  const dcerts = (certs: string[] | undefined) => {
    if (!certs || certs.length === 0) return null;
    if (certs.includes('none')) return 'Keine';
    return certs.map((c) => CERT_LABELS[c as CpCertification] ?? c).join(', ');
  };

  const ddate = (d: string | null | undefined) =>
    d ? new Date(d).toLocaleDateString('de-DE') : null;

  const isNis2Saved = profile?.regulationType === 'nis2_essential' || profile?.regulationType === 'nis2_important';

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className={styles.root}>

      {/* ── Summary Bar ── */}
      <div className={styles.summaryBar}>

        {/* Zeile 1: Sub-Score-Cards + Gesamt */}
        <div className={styles.summaryScoresRow}>
          <SubScoreCard label="ISMS" score={ismsScore} max={30} />
          <SubScoreCard label="BCMS" score={bcmsScore} max={20} />
          <SubScoreCard label="Zertifizierungen" score={certScore} max={30} />
          <SubScoreCard label="Letztes Audit" score={auditScore} max={20} />

          {/* Gesamt-Score */}
          <div className={styles.totalScoreCard} style={{ borderColor: `${scoreColor}40` }}>
            <div style={{ position: 'relative', width: 48, height: 48, flexShrink: 0 }}>
              <ScoreRing score={score} color={scoreColor} />
              <span style={{
                position: 'absolute', inset: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.875rem', fontWeight: 700, color: scoreColor,
              }}>
                {score}
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.125rem' }}>
              <span className={styles.panelHeadline}>Gesamt</span>
              <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>/ 100 Punkte</span>
              <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: scoreColor }}>{scoreLabel}</span>
            </div>
          </div>
        </div>

        {/* Zeile 2: Datenqualität + Größenklasse */}
        <div className={styles.summaryMetaRow}>
          <div className={styles.qualityPanel}>
            <span className={styles.panelHeadline}>Datenqualität</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{ flex: 1, height: 5, background: 'var(--color-border)', borderRadius: 999, overflow: 'hidden', minWidth: 80 }}>
                <div style={{
                  height: '100%', borderRadius: 999,
                  background: knownCount >= 11 ? '#16a34a' : knownCount >= 7 ? '#d97706' : '#dc2626',
                  width: `${(knownCount / 16) * 100}%`,
                  transition: 'width 0.3s',
                }} />
              </div>
              <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                {knownCount} / 16 bestätigt
              </span>
            </div>
          </div>

          <div className={styles.summaryDivider} />

          <div className={styles.sizePanel}>
            <span className={styles.panelHeadline}>Größenklasse</span>
            <span style={{
              fontSize: '0.8125rem', fontWeight: 700,
              padding: '0.125rem 0.625rem', borderRadius: 999,
              background: SIZE_COLORS[liveSizeClass].bg,
              color: SIZE_COLORS[liveSizeClass].color,
              alignSelf: 'flex-start',
            }}>
              {SIZE_LABELS[liveSizeClass]}
            </span>
            {profile?.sizeClass != null && profile.sizeClass !== liveSizeClass && (
              <span style={{ fontSize: '0.6875rem', color: '#d97706' }}>
                gespeichert: {SIZE_LABELS[profile.sizeClass]}
              </span>
            )}
          </div>
        </div>

      </div>

      {/* ── Read-only display header ── */}
      <div className={styles.displayHeader}>
        <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>
          {profile
            ? `Zuletzt aktualisiert: ${new Date(profile.updatedAt).toLocaleDateString('de-DE')}`
            : 'Noch kein Compliance-Profil vorhanden'}
        </span>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {profile && (
            <button
              onClick={() => setDeleteOpen(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.3rem',
                padding: '0.3rem 0.75rem', borderRadius: '6px', border: '1px solid #fca5a5',
                background: 'transparent', color: '#dc2626', cursor: 'pointer',
                fontSize: '0.8125rem', fontWeight: 500,
              }}
            >
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 13 6" />
                <path d="M8 6V3M5 6l.5 8h5L11 6" />
                <path d="M4 3h8" />
              </svg>
              Löschen
            </button>
          )}
          <button className={styles.editButton} onClick={handleOpenEdit}>
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11.5 2.5a2.121 2.121 0 013 3L5 15H1v-4L11.5 2.5z" />
            </svg>
            {profile ? 'Bearbeiten' : 'Profil anlegen'}
          </button>
        </div>
      </div>

      {/* ── Read-only display: 3-column info-card grid ── */}
      {!profile ? (
        <div className={styles.emptyState}>
          Noch kein Compliance-Profil vorhanden. Klicken Sie auf „Profil anlegen", um zu beginnen.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', alignItems: 'start' }}>
          <ProfileSection title="Regulierung &amp; Sektor">
            <ProfileDisplayRow label="Branche / Sektor" value={dn(profile.industrySector, INDUSTRY_SECTOR_LABELS)} quality={pq('industrySector')} />
            <ProfileDisplayRow label="Regulierungsart" value={dn(profile.regulationType, REG_TYPE_LABELS)} quality={pq('regulationType')} />
            {isNis2Saved && (
              <ProfileDisplayRow label="NIS2-Sektor" value={dn(profile.nis2Sector, NIS2_SECTOR_LABELS)} quality={pq('nis2Sector')} />
            )}
            <ProfileDisplayRow label="BaFin-beaufsichtigt" value={db(profile.bafinSupervised)} quality={pq('bafinSupervised')} />
            <ProfileDisplayRow label="DORA anwendbar" value={dn(profile.doraApplicable, DORA_LABELS)} quality={pq('doraApplicable')} />
            <ProfileDisplayRow label="CRA-Produkthersteller" value={db(profile.isDigitalProductManufacturer)} quality={pq('isDigitalProductManufacturer')} />
          </ProfileSection>

          <ProfileSection title="Größenklasse &amp; Struktur">
            <ProfileDisplayRow label="Mitarbeiterzahl" value={dn(profile.employeeCountRange, EMP_LABELS)} quality={pq('employeeCountRange')} />
            <ProfileDisplayRow label="Jahresumsatz" value={dn(profile.revenueRange, REV_LABELS)} quality={pq('revenueRange')} />
            <ProfileDisplayRow label="Bilanzsumme" value={dn(profile.balanceSheetRange, BAL_LABELS)} quality={pq('balanceSheetRange')} />
            <ProfileDisplayRow label="Standortbereich" value={dn(profile.locationScope, LOC_LABELS)} quality={pq('locationScope')} />
            <ProfileDisplayRow label="Kritische Funktionen" value={profile.criticalFunctionsCount != null ? String(profile.criticalFunctionsCount) : null} quality={pq('criticalFunctionsCount')} />
            <ProfileDisplayRow label="Kritische IKT-Anbieter" value={profile.criticalIctProvidersCount != null ? String(profile.criticalIctProvidersCount) : null} quality={pq('criticalIctProvidersCount')} />
          </ProfileSection>

          <ProfileSection title="IT-Reifegrad">
            <ProfileDisplayRow label="ISMS-Status" value={dn(profile.ismsStatus, ISMS_LABELS)} quality={pq('ismsStatus')} />
            <ProfileDisplayRow label="BCMS-Status" value={dn(profile.bcmsStatus, BCMS_LABELS)} quality={pq('bcmsStatus')} />
            <ProfileDisplayRow label="Zertifizierungen" value={dcerts(profile.certifications)} quality={pq('certifications')} />
            <ProfileDisplayRow label="Letztes Audit" value={ddate(profile.lastAuditDate)} quality={pq('lastAuditDate')} />
          </ProfileSection>
        </div>
      )}

      {/* ── Delete Confirmation Modal ── */}
      <Modal
        isOpen={isDeleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Compliance-Profil löschen"
        maxWidth="420px"
      >
        <p style={{ margin: '0 0 1.5rem', color: 'var(--color-text)', fontSize: '0.9375rem', lineHeight: 1.5 }}>
          Möchten Sie das Compliance-Profil dieses Unternehmens wirklich löschen? Alle erfassten Daten und der Änderungsverlauf gehen unwiderruflich verloren.
        </p>
        <div className={styles.modalFooter} style={{ margin: '0 -1.5rem -1.5rem' }}>
          <button className={styles.cancelButton} onClick={() => setDeleteOpen(false)}>
            Abbrechen
          </button>
          <button
            onClick={handleDeleteConfirm}
            disabled={deleteMutation.isPending}
            style={{
              padding: '0.5rem 1.25rem', borderRadius: '8px', border: 'none',
              background: '#dc2626', color: '#fff', cursor: 'pointer',
              fontSize: '0.875rem', fontWeight: 600, opacity: deleteMutation.isPending ? 0.6 : 1,
            }}
          >
            {deleteMutation.isPending ? 'Löschen…' : 'Endgültig löschen'}
          </button>
        </div>
      </Modal>

      {/* ── Edit Modal ── */}
      <Modal
        isOpen={isEditOpen}
        onClose={handleCloseEdit}
        title={profile ? 'Compliance-Profil bearbeiten' : 'Compliance-Profil anlegen'}
        maxWidth="760px"
      >
        {/* Section Sub-Tabs */}
        <div className={styles.sectionTabBar} style={{ margin: '-1.5rem -1.5rem 1rem', padding: '0 1.5rem' }}>
          {SECTION_TABS.map((tab) => (
            <button
              key={tab.id}
              className={`${styles.sectionTab} ${activeSection === tab.id ? styles.sectionTabActive : ''}`}
              onClick={() => setActiveSection(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Section content */}
        <div className={styles.section}>

          {/* Regulierung */}
          {activeSection === 'regulierung' && (
            <>
              <p className={styles.sectionIntro}>
                Regulatorische Einordnung des Unternehmens — Grundlage für die Ermittlung anwendbarer Pflichten aus NIS2, DORA, CRA und BaFin-Regulierung.
              </p>
              <FieldCard
                label="Branche / Sektor"
                help="Wirtschaftssektor des Unternehmens — bestimmt den regulatorischen Rahmen (NIS2-Anhang, KRITIS, CRA)"
                quality={q('industrySectorQuality')}
                onQuality={(v) => set('industrySectorQuality', v)}
                valueNode={
                  <EnumSelect
                    value={form.industrySector ?? undefined}
                    options={Object.keys(INDUSTRY_SECTOR_LABELS) as CpIndustrySector[]}
                    labels={INDUSTRY_SECTOR_LABELS}
                    placeholder="— bitte wählen —"
                    onChange={(v) => set('industrySector', v)}
                  />
                }
              />
              <FieldCard
                label="Regulierungsart"
                help="Regulierungsrahmen, dem das Unternehmen primär unterliegt"
                quality={q('regulationTypeQuality')}
                onQuality={(v) => set('regulationTypeQuality', v)}
                valueNode={
                  <EnumSelect
                    value={form.regulationType ?? undefined}
                    options={['financial_institution', 'ict_provider_financial', 'nis2_essential', 'nis2_important', 'unregulated', 'unknown'] as CpRegulationType[]}
                    labels={REG_TYPE_LABELS}
                    placeholder="— bitte wählen —"
                    onChange={(v) => set('regulationType', v)}
                  />
                }
              />
              {isNis2 && (
                <FieldCard
                  label="NIS2-Sektor"
                  help="Betroffener Sektor gemäß NIS2-Anhang I (wesentlich) oder II (wichtig)"
                  quality={q('nis2SectorQuality')}
                  onQuality={(v) => set('nis2SectorQuality', v)}
                  valueNode={
                    <EnumSelect
                      value={form.nis2Sector ?? undefined}
                      options={Object.keys(NIS2_SECTOR_LABELS) as CpNis2Sector[]}
                      labels={NIS2_SECTOR_LABELS}
                      placeholder="— bitte wählen —"
                      onChange={(v) => set('nis2Sector', v)}
                    />
                  }
                />
              )}
              <FieldCard
                label="BaFin-beaufsichtigt"
                help="Steht das Unternehmen unter direkter Aufsicht der BaFin?"
                quality={q('bafinSupervisedQuality')}
                onQuality={(v) => set('bafinSupervisedQuality', v)}
                valueNode={
                  <select
                    value={form.bafinSupervised === undefined ? '' : String(form.bafinSupervised)}
                    onChange={(e) => set('bafinSupervised', e.target.value === '' ? undefined : e.target.value === 'true')}
                    className={styles.fieldSelect}
                    style={{ width: '100%', maxWidth: 'none' }}
                  >
                    <option value="">— bitte wählen —</option>
                    <option value="true">Ja</option>
                    <option value="false">Nein</option>
                  </select>
                }
              />
              <FieldCard
                label="DORA anwendbar"
                help="Gilt DORA direkt (Finanzentität) oder indirekt als IKT-Drittdienstleister?"
                quality={q('doraApplicableQuality')}
                onQuality={(v) => set('doraApplicableQuality', v)}
                valueNode={
                  <EnumSelect
                    value={form.doraApplicable ?? undefined}
                    options={['yes', 'no', 'indirect'] as CpDoraApplicable[]}
                    labels={DORA_LABELS}
                    placeholder="— bitte wählen —"
                    onChange={(v) => set('doraApplicable', v)}
                  />
                }
              />
              <FieldCard
                label="CRA-Produkthersteller"
                help="Stellt das Unternehmen Produkte mit digitalen Elementen her oder vertreibt es solche? (Cyber Resilience Act)"
                quality={q('isDigitalProductManufacturerQuality')}
                onQuality={(v) => set('isDigitalProductManufacturerQuality', v)}
                valueNode={
                  <select
                    value={form.isDigitalProductManufacturer === undefined ? '' : String(form.isDigitalProductManufacturer)}
                    onChange={(e) => set('isDigitalProductManufacturer', e.target.value === '' ? undefined : e.target.value === 'true')}
                    className={styles.fieldSelect}
                    style={{ width: '100%', maxWidth: 'none' }}
                  >
                    <option value="">— bitte wählen —</option>
                    <option value="true">Ja — CRA anwendbar</option>
                    <option value="false">Nein</option>
                  </select>
                }
              />
            </>
          )}

          {/* Größenklasse */}
          {activeSection === 'groesse' && (
            <>
              <p className={styles.sectionIntro}>
                EU-Schwellenwerte für Mitarbeiterzahl, Umsatz und Bilanzsumme bestimmen die Größenklasse (S/M/L) — maßgeblich für NIS2-Einordnung und Verhältnismäßigkeit der Anforderungen.
              </p>
              <FieldCard
                label="Mitarbeiterzahl"
                help="Vollzeitäquivalente (FTE) im Jahresdurchschnitt"
                quality={q('employeeCountRangeQuality')}
                onQuality={(v) => set('employeeCountRangeQuality', v)}
                valueNode={
                  <EnumSelect
                    value={form.employeeCountRange ?? undefined}
                    options={['lt_10', 'e10_49', 'e50_249', 'e250_999', 'gte_1000'] as CpEmployeeCountRange[]}
                    labels={EMP_LABELS}
                    placeholder="— bitte wählen —"
                    onChange={(v) => set('employeeCountRange', v)}
                  />
                }
              />
              <FieldCard
                label="Jahresumsatz"
                help="Nettoumsatz des letzten abgeschlossenen Geschäftsjahres"
                quality={q('revenueRangeQuality')}
                onQuality={(v) => set('revenueRangeQuality', v)}
                valueNode={
                  <EnumSelect
                    value={form.revenueRange ?? undefined}
                    options={['lt_2m', 'r2m_10m', 'r10m_50m', 'r50m_250m', 'gte_250m'] as CpRevenueRange[]}
                    labels={REV_LABELS}
                    placeholder="— bitte wählen —"
                    onChange={(v) => set('revenueRange', v)}
                  />
                }
              />
              <FieldCard
                label="Bilanzsumme"
                help="Gesamte Bilanzsumme des letzten abgeschlossenen Geschäftsjahres"
                quality={q('balanceSheetRangeQuality')}
                onQuality={(v) => set('balanceSheetRangeQuality', v)}
                valueNode={
                  <EnumSelect
                    value={form.balanceSheetRange ?? undefined}
                    options={['lt_2m', 'b2m_10m', 'b10m_43m', 'b43m_250m', 'gte_250m'] as CpBalanceSheetRange[]}
                    labels={BAL_LABELS}
                    placeholder="— bitte wählen —"
                    onChange={(v) => set('balanceSheetRange', v)}
                  />
                }
              />
            </>
          )}

          {/* Struktur */}
          {activeSection === 'struktur' && (
            <>
              <p className={styles.sectionIntro}>
                Organisationsstruktur und geografische Reichweite — relevant für Meldepflichten, grenzüberschreitende Koordination und DORA-Anforderungen an kritische IKT-Abhängigkeiten.
              </p>
              <FieldCard
                label="Standortbereich"
                help="Geografische Reichweite der wesentlichen Unternehmensaktivitäten"
                quality={q('locationScopeQuality')}
                onQuality={(v) => set('locationScopeQuality', v)}
                valueNode={
                  <EnumSelect
                    value={form.locationScope ?? undefined}
                    options={['de_only', 'eu', 'global'] as CpLocationScope[]}
                    labels={LOC_LABELS}
                    placeholder="— bitte wählen —"
                    onChange={(v) => set('locationScope', v)}
                  />
                }
              />
              <FieldCard
                label="Kritische Funktionen"
                help="Anzahl formell definierter kritischer Geschäftsfunktionen (gem. DORA / NIS2)"
                quality={q('criticalFunctionsCountQuality')}
                onQuality={(v) => set('criticalFunctionsCountQuality', v)}
                valueNode={
                  <input
                    type="number"
                    min={0}
                    value={form.criticalFunctionsCount ?? ''}
                    onChange={(e) => set('criticalFunctionsCount', e.target.value === '' ? undefined : Number(e.target.value))}
                    className={styles.numberInput}
                    placeholder="Anzahl"
                  />
                }
              />
              <FieldCard
                label="Kritische IKT-Anbieter"
                help="Anzahl als kritisch eingestufter IKT-Drittdienstleister im Sinne von DORA Art. 28"
                quality={q('criticalIctProvidersCountQuality')}
                onQuality={(v) => set('criticalIctProvidersCountQuality', v)}
                valueNode={
                  <input
                    type="number"
                    min={0}
                    value={form.criticalIctProvidersCount ?? ''}
                    onChange={(e) => set('criticalIctProvidersCount', e.target.value === '' ? undefined : Number(e.target.value))}
                    className={styles.numberInput}
                    placeholder="Anzahl"
                  />
                }
              />
            </>
          )}

          {/* IT-Reifegrad */}
          {activeSection === 'reifegrad' && (
            <>
              <p className={styles.sectionIntro}>
                IT-Sicherheitsreifegrad des Unternehmens — fließt direkt in den Reifegrad-Score ein. ISMS, BCMS, Zertifizierungen und Audit-Aktualität bestimmen die Bewertung (max. 100 Punkte).
              </p>

              <div className={styles.scoreDetailBox}>
                <div className={styles.scoreDetailTitle}>Score-Aufschlüsselung</div>
                <div className={styles.scoreDetailGrid}>
                  <span>ISMS-Status</span>
                  <div className={styles.scoreDetailBar}>
                    <div style={{ height: '100%', borderRadius: 999, background: scoreColor, width: `${(ismsScore / 30) * 100}%`, transition: 'width 0.4s ease' }} />
                  </div>
                  <span className={styles.scoreDetailValue}>{ismsScore} / 30</span>

                  <span>BCMS-Status</span>
                  <div className={styles.scoreDetailBar}>
                    <div style={{ height: '100%', borderRadius: 999, background: scoreColor, width: `${(bcmsScore / 20) * 100}%`, transition: 'width 0.4s ease' }} />
                  </div>
                  <span className={styles.scoreDetailValue}>{bcmsScore} / 20</span>

                  <span>Zertifizierungen</span>
                  <div className={styles.scoreDetailBar}>
                    <div style={{ height: '100%', borderRadius: 999, background: scoreColor, width: `${(certScore / 30) * 100}%`, transition: 'width 0.4s ease' }} />
                  </div>
                  <span className={styles.scoreDetailValue}>{certScore} / 30</span>

                  <span>Letztes Audit</span>
                  <div className={styles.scoreDetailBar}>
                    <div style={{ height: '100%', borderRadius: 999, background: scoreColor, width: `${(auditScore / 20) * 100}%`, transition: 'width 0.4s ease' }} />
                  </div>
                  <span className={styles.scoreDetailValue}>{auditScore} / 20</span>
                </div>
                <div className={styles.scoreDetailTotal} style={{ color: scoreColor }}>
                  Gesamt: {score} / 100 — {scoreLabel}
                </div>
              </div>

              <FieldCard
                label="ISMS-Status"
                help="Reifegrad des Informationssicherheits-Managementsystems (max. 30 Pkt.)"
                quality={q('ismsStatusQuality')}
                onQuality={(v) => set('ismsStatusQuality', v)}
                scoreText={`${ismsScore} / 30`}
                valueNode={
                  <EnumSelect
                    value={form.ismsStatus ?? undefined}
                    options={['not_existing', 'documented', 'certified'] as CpIsmsStatus[]}
                    labels={ISMS_LABELS}
                    placeholder="— bitte wählen —"
                    onChange={(v) => set('ismsStatus', v)}
                  />
                }
              />
              <FieldCard
                label="BCMS-Status"
                help="Reifegrad des Business-Continuity-Managementsystems (max. 20 Pkt.)"
                quality={q('bcmsStatusQuality')}
                onQuality={(v) => set('bcmsStatusQuality', v)}
                scoreText={`${bcmsScore} / 20`}
                valueNode={
                  <EnumSelect
                    value={form.bcmsStatus ?? undefined}
                    options={['not_existing', 'documented', 'certified'] as CpBcmsStatus[]}
                    labels={BCMS_LABELS}
                    placeholder="— bitte wählen —"
                    onChange={(v) => set('bcmsStatus', v)}
                  />
                }
              />
              <FieldCard
                label="Zertifizierungen"
                help="Aktive Zertifizierungen und Prüfberichte — je Zertifikat 10 Punkte (max. 30 Pkt.)"
                quality={q('certificationsQuality')}
                onQuality={(v) => set('certificationsQuality', v)}
                scoreText={`${certScore} / 30`}
                valueNode={
                  <div className={styles.checkboxGroup}>
                    {(Object.keys(CERT_LABELS) as CpCertification[]).map((cert) => {
                      const selected = form.certifications ?? [];
                      const hasNone = selected.includes('none');
                      const hasOther = selected.some((c) => c !== 'none');
                      const disabled = cert === 'none' ? hasOther : hasNone;
                      return (
                        <label key={cert} className={`${styles.checkboxLabel} ${disabled ? styles.checkboxLabelDisabled : ''}`}>
                          <input
                            type="checkbox"
                            checked={selected.includes(cert)}
                            disabled={disabled}
                            onChange={(e) => {
                              if (cert === 'none') {
                                set('certifications', e.target.checked ? ['none'] : []);
                              } else {
                                const withoutNone = selected.filter((c) => c !== 'none');
                                set('certifications', e.target.checked
                                  ? [...withoutNone, cert]
                                  : withoutNone.filter((c) => c !== cert));
                              }
                            }}
                          />
                          {CERT_LABELS[cert]}
                        </label>
                      );
                    })}
                  </div>
                }
              />
              <FieldCard
                label="Letztes Audit"
                help="Datum des letzten externen oder internen Sicherheitsaudits (max. 20 Pkt.)"
                quality={q('lastAuditDateQuality')}
                onQuality={(v) => set('lastAuditDateQuality', v)}
                scoreText={`${auditScore} / 20`}
                valueNode={
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                    <input
                      type="date"
                      value={form.lastAuditDate ?? ''}
                      onChange={(e) => set('lastAuditDate', e.target.value || undefined)}
                      className={styles.dateInput}
                    />
                    <label className={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={!form.lastAuditDate}
                        onChange={(e) => { if (e.target.checked) set('lastAuditDate', undefined); }}
                      />
                      Kein Audit durchgeführt
                    </label>
                  </div>
                }
              />
            </>
          )}
        </div>

        {/* Modal footer */}
        <div className={styles.modalFooter} style={{ margin: '1.5rem -1.5rem -1.5rem' }}>
          {saveError && <span style={{ color: '#dc2626', fontSize: '0.875rem' }}>{saveError}</span>}
          <button className={styles.cancelButton} onClick={handleCloseEdit}>Abbrechen</button>
          <button
            onClick={handleSave}
            disabled={!isDirty || upsert.isPending}
            className={styles.saveButton}
          >
            {upsert.isPending ? 'Speichern…' : 'Speichern'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
